import { ctaData, contactInfo } from '@/mocks/homeData';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

const FORM_ACTION_URL = 'https://formspree.io/f/mgaoyonp';

export default function CTASection() {
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = (formData.get('website_alt') as string || '').trim();
    if (honeypot) {
      setFormStatus('success');
      setFormMessage('Mensagem enviada com sucesso! Entraremos em contato em breve.');
      return;
    }

    setFormStatus('loading');

    try {
      const res = await fetch(FORM_ACTION_URL, {
        method: 'POST',
        body: formData,
        headers: { Accept: 'application/json' },
      });

      if (res.ok) {
        setFormStatus('success');
        setFormMessage('Mensagem enviada com sucesso! Entraremos em contato em breve.');
        form.reset();
        setTimeout(() => navigate('/contato-recebido'), 800);
      } else {
        let errorMessage = 'Erro ao enviar. Tente novamente.';
        try {
          const data = await res.json();
          if (Array.isArray(data.errors) && data.errors.length) {
            errorMessage = data.errors.map((item: { message?: string }) => item.message || '').filter(Boolean).join(' ');
          }
        } catch { /* mantém mensagem padrão */ }
        setFormStatus('error');
        setFormMessage(errorMessage || 'Erro ao enviar. Tente novamente.');
      }
    } catch {
      setFormStatus('error');
      setFormMessage('Erro de conexão. Tente novamente.');
    }
  };

  return (
    <section id="cta" className="py-20 md:py-28 bg-background-50">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="flex flex-col lg:flex-row gap-12 lg:gap-16 items-center">
          <div className="w-full lg:w-1/2 text-center lg:text-left">
            <span className="text-xs uppercase tracking-[0.2em] text-primary-500 font-semibold mb-3 inline-block">
              Agende sua consulta
            </span>
            <h2 className="font-heading text-3xl md:text-5xl font-bold text-foreground-950 leading-tight mb-4">
              {ctaData.title}
            </h2>
            <p className="text-foreground-600 text-base leading-relaxed mb-8">
              {ctaData.subtitle}
            </p>

            <div className="flex flex-col sm:flex-row gap-3 mb-8">
              <a
                href={`https://wa.me/${contactInfo.whatsapp}?text=Olá!%20Gostaria%20de%20agendar%20uma%20avaliação.`}
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="whitespace-nowrap bg-[#25D366] hover:bg-[#20bd5a] text-white text-sm font-semibold px-10 py-4 rounded-full transition-all duration-300 cursor-pointer text-center"
              >
                <i className="ri-whatsapp-line mr-2"></i>
                Agendar pelo WhatsApp
              </a>
              <a
                href={`tel:${contactInfo.phone}`}
                className="whitespace-nowrap border border-foreground-200 text-foreground-700 hover:border-foreground-400 text-sm font-medium px-10 py-4 rounded-full transition-all duration-300 cursor-pointer text-center"
              >
                <i className="ri-phone-line mr-2"></i>
                Ligar Agora
              </a>
            </div>

            <div className="flex items-center gap-4 justify-center lg:justify-start text-xs text-foreground-400">
              <span><i className="ri-shield-check-line mr-1 text-accent-500"></i> Dados protegidos</span>
              <span><i className="ri-time-line mr-1 text-accent-500"></i> Resposta em até 15 min</span>
            </div>
          </div>

          <div className="w-full lg:w-1/2">
            <div className="bg-background-100 rounded-2xl p-6 md:p-8">
              <h3 className="font-heading text-xl font-bold text-foreground-950 mb-1">Solicite sua avaliação</h3>
              <p className="text-foreground-500 text-sm mb-6">Preencha o formulário e retornaremos em breve.</p>

              <form onSubmit={handleSubmit} id="lead-form" className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="nome" className="block text-xs font-medium text-foreground-600 mb-1.5">
                      Nome completo *
                    </label>
                    <input
                      type="text"
                      id="nome"
                      name="nome"
                      required
                      className="w-full bg-background-50 border border-background-200 rounded-lg px-4 py-3 text-sm text-foreground-950 outline-none focus:border-accent-400 transition-colors placeholder:text-foreground-300"
                      placeholder="Seu nome"
                    />
                  </div>
                  <div>
                    <label htmlFor="whatsapp" className="block text-xs font-medium text-foreground-600 mb-1.5">
                      WhatsApp *
                    </label>
                    <input
                      type="tel"
                      id="whatsapp"
                      name="whatsapp"
                      required
                      className="w-full bg-background-50 border border-background-200 rounded-lg px-4 py-3 text-sm text-foreground-950 outline-none focus:border-accent-400 transition-colors placeholder:text-foreground-300"
                      placeholder="(11) 9XXXX-XXXX"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="email" className="block text-xs font-medium text-foreground-600 mb-1.5">
                    E-mail
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    className="w-full bg-background-50 border border-background-200 rounded-lg px-4 py-3 text-sm text-foreground-950 outline-none focus:border-accent-400 transition-colors placeholder:text-foreground-300"
                    placeholder="seu@email.com"
                  />
                </div>

                <div>
                  <label htmlFor="interesse" className="block text-xs font-medium text-foreground-600 mb-1.5">
                    Tratamento de interesse *
                  </label>
                  <select
                    id="interesse"
                    name="interesse"
                    required
                    className="w-full bg-background-50 border border-background-200 rounded-lg px-4 py-3 text-sm text-foreground-950 outline-none focus:border-accent-400 transition-colors cursor-pointer appearance-none"
                    defaultValue=""
                  >
                    <option value="" disabled>Selecione o tratamento</option>
                    <option value="cirurgia-bucomaxilofacial">Cirurgia Bucomaxilofacial</option>
                    <option value="extracao-de-siso">Extração de Siso</option>
                    <option value="invisalign">Invisalign</option>
                    <option value="odontopediatria">Odontopediatria</option>
                    <option value="clareamento-dental">Clareamento Dental</option>
                    <option value="implantes-dentarios">Implantes Dentários</option>
                    <option value="facetas">Facetas Dentárias</option>
                    <option value="avaliacao-odontologica">Avaliação Odontológica</option>
                    <option value="outro">Outro</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="mensagem" className="block text-xs font-medium text-foreground-600 mb-1.5">
                    Mensagem (opcional)
                  </label>
                  <textarea
                    id="mensagem"
                    name="mensagem"
                    rows={3}
                    maxLength={500}
                    className="w-full bg-background-50 border border-background-200 rounded-lg px-4 py-3 text-sm text-foreground-950 outline-none focus:border-accent-400 transition-colors placeholder:text-foreground-300 resize-none"
                    placeholder="Conte-nos um pouco sobre o que você precisa..."
                  ></textarea>
                </div>

                <div className="antispam">
                  <input
                    type="text"
                    name="website_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />
                </div>

                <button
                  type="submit"
                  disabled={formStatus === 'loading'}
                  className="whitespace-nowrap w-full bg-primary-500 hover:bg-primary-600 disabled:bg-primary-300 text-white text-sm font-semibold px-8 py-3.5 rounded-full transition-all duration-300 cursor-pointer"
                >
                  {formStatus === 'loading' ? 'Enviando...' : 'Solicitar Avaliação'}
                </button>

                {formStatus === 'success' && (
                  <p className="text-accent-600 text-sm text-center font-medium">{formMessage}</p>
                )}
                {formStatus === 'error' && (
                  <p className="text-red-500 text-sm text-center">{formMessage}</p>
                )}
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}